import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import {
  type CreateUserProgramInputDto,
  type UpdateUserProgramInputDto,
} from "@modules/programs/gateway/programInputDto"
import { ProgramStatus, ProgramType, ProgramVisibility, type SharedCoach } from "@modules/programs/types/programTypes"

export class UserProgramDraftEntity {
  id?: string
  type: ProgramType
  name: string
  visibility: boolean // Take care it's a boolean, not a ProgramVisibility. Because we need boolean for the switch in the form
  description: string
  image: MediaItemFormType | null
  defaultHideSessionContent: boolean
  scoreboardLocked: boolean
  entranceCheckEnabled: boolean
  price?: PriceObjectEntity // Price is not use ?
  duration?: number
  status: ProgramStatus
  sharedCoaches?: SharedCoach[]
  programIdToDuplicate?: string

  private constructor(program: UserProgramDraftEntity) {
    this.id = program.id
    this.type = program.type
    this.name = program.name
    this.visibility = program.visibility
    this.description = program.description
    this.image = program.image
    this.defaultHideSessionContent = program.defaultHideSessionContent
    this.scoreboardLocked = program.scoreboardLocked
    this.entranceCheckEnabled = program.entranceCheckEnabled
    this.price = program.price
    this.duration = program.duration
    this.status = program.status
    this.sharedCoaches = program.sharedCoaches
    this.programIdToDuplicate = program.programIdToDuplicate
  }

  static createDraft(programType: ProgramType, program?: UserProgramDetailsEntity): UserProgramDraftEntity {
    return new UserProgramDraftEntity({
      id: program?.id ?? undefined,
      type: program?.type ?? programType,
      name: program?.name ?? "",
      visibility: program?.visibility === ProgramVisibility.PUBLIC,
      description: program?.description ?? "",
      image: program?.image
        ? {
            url: program.image.url,
            key: program.image.key ?? null,
            type: MediaType.S3_IMAGE,
            filename: null,
            isUploading: false,
            file: null,
          }
        : null,
      defaultHideSessionContent: program?.defaultHideSessionContent ?? false,
      scoreboardLocked: program?.scoreboardLocked ?? false,
      entranceCheckEnabled: program?.entranceCheckEnabled ?? false,
      price: program?.price ? PriceObjectEntity.fromDto(program.price) : undefined,
      duration: program?.duration ?? undefined,
      status: program?.status ?? ProgramStatus.DRAFT,
      sharedCoaches: program?.sharedCoaches ?? undefined,
      programIdToDuplicate: program?.id ?? undefined,
    })
  }

  static formikToCreateUserProgramInputDto(formik: UserProgramDraftEntity): CreateUserProgramInputDto {
    return {
      name: formik.name,
      description: formik.description,
      imageKey: formik.image?.key ?? null,
      visibility: formik.visibility ? ProgramVisibility.PUBLIC : ProgramVisibility.PRIVATE,
      entranceCheckEnabled: formik.entranceCheckEnabled,
      defaultHideSessionContent: formik.defaultHideSessionContent,
      sharedCoaches: formik.sharedCoaches?.map((coach) => coach.user.id) ?? null,
      scoreboardVisibility: formik.scoreboardLocked ? false : true,
      type: formik.type,
      duration: formik.type === ProgramType.ONOFF ? (formik.duration ?? null) : null,
      programIdToDuplicate: formik.type === ProgramType.ONOFF ? (formik.programIdToDuplicate ?? null) : null,
    }
  }

  static formikToUpdateUserProgramInputDto(formik: UserProgramDraftEntity): UpdateUserProgramInputDto {
    return {
      name: formik.name,
      description: formik.description,
      imageKey: formik.image?.key ?? null,
      visibility: formik.visibility ? ProgramVisibility.PUBLIC : ProgramVisibility.PRIVATE,
      entranceCheckEnabled: formik.entranceCheckEnabled,
      defaultHideSessionContent: formik.defaultHideSessionContent,
      scoreboardVisibility: formik.scoreboardLocked ? false : true,
      sharedCoaches: formik.sharedCoaches?.map((coach) => coach.user.id) ?? null,
      duration: formik.type === ProgramType.ONOFF ? (formik.duration ?? null) : null,
    }
  }
}
